import { useEffect, useState } from 'react';
import { InfoCard, Progress, StatusError, StatusOK, StatusWarning, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

const PIPELINE_REPO = 'deuna-devops/deuna-tpl-do-pipeline-microservice';
// Lo último en runtime es lo que trae cada plantilla msa.
const TEMPLATE = { node: 'deuna-platform/deuna-msa-template-repository', bun: 'deuna-platform/deuna-msa-bun-template-repository' };
const INTERNAL = /^@deuna(-platform)?\//;

type Row = { item: string; kind: string; uses: string; latest: string; status: 'ok' | 'behind' | 'major' | 'unknown' };

const parts = (v: string) => v.replace(/^[^\d]*/, '').split(/[.-]/).map(n => parseInt(n, 10) || 0);
/** -1 si a < b, 0 si igual, 1 si a > b (numérico por partes: 20261001.10 > 20261001.9). */
export const compare = (a: string, b: string) => {
  const [x, y] = [parts(a), parts(b)];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) < (y[i] ?? 0) ? -1 : 1;
  }
  return 0;
};
export const statusOf = (uses: string, latest?: string | null): Row['status'] => {
  if (!latest || !uses) return 'unknown';
  if (compare(uses, latest) >= 0) return 'ok';
  return parts(uses)[0] < parts(latest)[0] ? 'major' : 'behind';
};

const STATUS = {
  ok: <StatusOK>Al día</StatusOK>,
  behind: <StatusWarning>Atrasada</StatusWarning>,
  major: <StatusError>Atrasada (versión mayor)</StatusError>,
  unknown: <>-</>,
};

const columns: TableColumn<Row>[] = [
  { title: 'Qué', field: 'item' },
  { title: 'Tipo', field: 'kind' },
  { title: 'Usa', field: 'uses' },
  { title: 'Última', field: 'latest' },
  { title: 'Estado', field: 'status', render: row => STATUS[row.status] },
];

/** Librerías internas, runtime y pipeline del código que está en prod (último tag v*) frente a lo último publicado. */
export const VersionsCard = () => {
  const { entity } = useEntity();
  const githubAuth = useApi(githubAuthApiRef);
  const discovery = useApi(discoveryApiRef);
  const { fetch: backendFetch } = useApi(fetchApiRef);
  const slug = entity.metadata.annotations?.['github.com/project-slug'];
  const [rows, setRows] = useState<Row[]>();
  const [ref, setRef] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const token = await githubAuth.getAccessToken(['repo']);
      const gh = async (path: string) => {
        const res = await fetch(`https://api.github.com/${path}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/vnd.github+json' },
        });
        return res.ok ? res.json() : undefined;
      };
      const file = async (repo: string, path: string, at?: string) => {
        const body = await gh(`repos/${repo}/contents/${path}${at ? `?ref=${encodeURIComponent(at)}` : ''}`);
        return body?.content ? atob(body.content.replace(/\n/g, '')) : undefined;
      };
      const newestTag = async (repo: string, prefix: string) => {
        const refs: { ref: string }[] = (await gh(`repos/${repo}/git/matching-refs/tags/${prefix}`)) ?? [];
        return refs.map(r => r.ref.replace('refs/tags/', '')).sort(compare).pop();
      };

      // Lo que corre en prod: el último tag v<fecha>; si no hay, qa (bv) y si no, la rama por defecto.
      const at = (await newestTag(slug, 'v2')) ?? (await newestTag(slug, 'bv2'));
      setRef(at ?? 'rama por defecto');
      const result: Row[] = [];

      const pkg = JSON.parse((await file(slug, 'package.json', at)) ?? '{}');
      const deps: Record<string, string> = { ...pkg.devDependencies, ...pkg.dependencies };
      const libs = Object.entries(deps).filter(([name]) => INTERNAL.test(name));
      if (libs.length) {
        const base = await discovery.getBaseUrl('package-versions');
        const res = await backendFetch(`${base}/latest?packages=${libs.map(([n]) => n).join(',')}`);
        const latest: Record<string, string | null> = res.ok ? await res.json() : {};
        for (const [name, range] of libs) {
          const uses = range.replace(/^[\^~]/, '');
          result.push({ item: name, kind: 'Librería', uses, latest: latest[name] ?? '?', status: statusOf(uses, latest[name]) });
        }
      }

      const dockerfile = (await file(slug, 'Dockerfile', at)) ?? '';
      const runtime = /BUN_VERSION=([\d.]+)/.test(dockerfile) ? 'bun' : 'node';
      const version = (text: string) =>
        text.match(runtime === 'bun' ? /BUN_VERSION=([\d.]+)/ : /NODE_VERSION=(\d+)/)?.[1] ??
        text.match(/FROM node:(\d+)/)?.[1] ??
        '';
      const latestRuntime = version((await file(TEMPLATE[runtime], 'Dockerfile')) ?? '');
      const usesRuntime = version(dockerfile);
      if (usesRuntime) {
        result.push({
          item: runtime === 'bun' ? 'Bun' : 'Node.js',
          kind: 'Runtime',
          uses: usesRuntime,
          latest: latestRuntime || '?',
          status: statusOf(usesRuntime, latestRuntime),
        });
      }

      const workflows: { name: string }[] = (await gh(`repos/${slug}/contents/.github/workflows${at ? `?ref=${encodeURIComponent(at)}` : ''}`)) ?? [];
      const latestPipeline = await newestTag(PIPELINE_REPO, 'v');
      for (const wf of workflows.filter(w => /\.ya?ml$/.test(w.name))) {
        const text = (await file(slug, `.github/workflows/${wf.name}`, at)) ?? '';
        for (const [, path, pinned] of text.matchAll(new RegExp(`${PIPELINE_REPO}/\\.github/workflows/([\\w.-]+)@([\\w.-]+)`, 'g'))) {
          // Excepción acordada: @v1 sigue la última v1.x.y, cuenta como al día si la última es v1.
          const floating = /^v\d+$/.test(pinned) && latestPipeline && parts(latestPipeline)[0] === parts(pinned)[0];
          result.push({
            item: `${wf.name} → ${path}`,
            kind: 'Pipeline',
            uses: pinned,
            latest: latestPipeline ?? '?',
            status: floating ? 'ok' : statusOf(pinned, latestPipeline),
          });
        }
      }
      setRows(result);
    })().catch(e => setError(String(e?.message ?? e)));
  }, [slug, githubAuth, discovery, backendFetch]);

  if (!slug) return null;
  if (error) return <WarningPanel title="No se pudieron revisar las versiones" message={error} />;
  if (!rows) return <InfoCard title="Versiones al día"><Progress /></InfoCard>;
  const behind = rows.filter(r => r.status === 'behind' || r.status === 'major').length;
  return (
    <Table<Row>
      title="Versiones al día"
      subtitle={`Revisado en ${ref}${behind ? ` · ${behind} por actualizar` : ' · todo al día'}`}
      options={{ paging: false, search: false, padding: 'dense' }}
      columns={columns}
      data={rows}
      emptyContent={<div style={{ padding: 16 }}>No se encontraron librerías internas, runtime ni pipeline.</div>}
    />
  );
};
